import React, { useCallback, useLayoutEffect } from 'react';
import { TEST_GRECAPTCHA } from '@sweetgreen/garnish';

import { useIsLoggedIn } from '@order/AuthMachine';
import { useCustomer } from '@order/Customer';
import { useCart } from '@order/hooks';
import { useFeatureFlag } from '@order/LaunchDarkly';
import { useTrackEffect } from '@order/Telemetry';

import { useRedirectAfterAuthStateChange } from '../../navigation';
import type { JoinScreenProps } from '../../navigation/AppNavigation.props';
import { JoinForm } from './subcomponents';
import type { JoinFormData } from './utils';

// ────────────────────────────────────────────────────────────────────────────────

export const JoinScreen = (props: JoinScreenProps) => {
  const { navigation, route } = props;

  // ─── Telemetry ───────────────────────────────────────────────────────

  const { isCartEmpty } = useCart();

  useTrackEffect('join.view', {
    entryPoint: isCartEmpty ? 'login' : 'checkout',
  });

  // ─── Navigation ──────────────────────────────────────────────────────

  const handlePressSignIn = useCallback(() => {
    const {
      redirect = 'home',
      redirectToPlanId,
      redirectToCampaignId,
    } = route.params ?? {};

    navigation.navigate('Login', {
      redirect,
      redirectToPlanId,
      redirectToCampaignId,
    });
  }, [route.params, navigation]);

  // ─── Redirect ────────────────────────────────────────────────────────

  const isLoggedIn = useIsLoggedIn();

  const redirectAfterAuthStateChange = useRedirectAfterAuthStateChange();

  useLayoutEffect(() => {
    if (isLoggedIn) redirectAfterAuthStateChange();
  }, [isLoggedIn, redirectAfterAuthStateChange]);

  // ─── Feature Flagging ────────────────────────────────────────────────

  const shouldUseJoinScreenV2 = useFeatureFlag(
    'CELS-1883-auth-migration-enabled',
  );

  if (shouldUseJoinScreenV2) {
    return <JoinScreenV2 handlePressSignIn={handlePressSignIn} {...props} />;
  }

  return <JoinScreenV1 handlePressSignIn={handlePressSignIn} {...props} />;
};

// To be removed together with CELS-1883-auth-migration-enabled.
export const JoinScreenV1 = (props: FeatureFlaggedJoinScreenProps) => {
  const { navigation, route, handlePressSignIn } = props;

  const {
    redirect = 'home',
    redirectToPlanId,
    redirectToCampaignId,
  } = route.params ?? {};

  const { signUp } = useCustomer();

  // ─────────────────────────────────────────────────────────────────

  const handleJoin = useCallback(
    async (input: JoinFormData) => {
      const {
        firstName,
        lastName,
        email,
        password,
        phoneNumber,
        birthday,
        grecaptcha = TEST_GRECAPTCHA,
      } = input;

      const response = await signUp({
        firstName,
        lastName,
        email,
        password,
        phoneNumber,
        birthday,
        grecaptcha,
      });

      const shouldVerifyNewUser = response?.__typename === 'RegisterSuccess';

      if (shouldVerifyNewUser) {
        navigation.replace('JoinVerification', {
          email,
          redirect,
          redirectToPlanId,
          redirectToCampaignId,
        });
      }
    },
    [navigation, redirect, redirectToPlanId, redirectToCampaignId, signUp],
  );

  // ────────────────────────────────────────────────────────────────────

  return (
    <JoinForm
      hasPassword
      shouldUseRecaptcha
      onPressJoin={handleJoin}
      onPressSignIn={handlePressSignIn}
    />
  );
};

export const JoinScreenV2 = (props: FeatureFlaggedJoinScreenProps) => {
  const { handlePressSignIn } = props;

  // ─────────────────────────────────────────────────────────────────

  const handleJoin = useCallback(async (input: JoinFormData) => {
    console.log('Joining with', input); // eslint-disable-line no-console
    // 1. Make a request to `createAccount` with Azure AD Access Token sent as header.
    // 2. Use the resulting refresh token to fetch a new valid access token.
    // 3. Exchange the refresh token for an access token.
    // 4. Store the refresh token in a cookie for the sweetgreen.com domain.
    // 5. Store the access token in memory.
  }, []);

  // ────────────────────────────────────────────────────────────────────

  return (
    <JoinForm
      isBirthdayRequired
      hasZipCode
      predefinedEmail="some@email.com"
      onPressJoin={handleJoin}
      onPressSignIn={handlePressSignIn}
    />
  );
};

// ─── Types ───────────────────────────────────────────────────────────────────

type FeatureFlaggedJoinScreenProps = JoinScreenProps & {
  handlePressSignIn: () => void;
};
